import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Download, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Icons } from "./ui/icons";
import { ScrollArea } from "./ui/scroll-area";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import UploadDocumentModal from "./upload-document-modal";
import PpeMaterialRequestNoteDrawer from "./ppe-material-request-note-drawer";
import PpeDetailsOfReceivedDrawer from "./ppe-details-of-received-drawer";
import SidePanel, { PanelHeader } from "./side-panel";
import { useIsMobile } from "../hooks/use-is-mobile";
import { cn } from "../lib/utils";
import {
  deletePpeRecordDocument,
  getPpeRecord,
  PpeProvision,
  PpePurchase,
  PpeRecordDocument,
  PpeRecordKind,
  uploadPpeRecordDocument,
} from "../lib/api";
import { getBackendErrorMessage } from "../lib/utils";

type PpeRecordDetailsDrawerProps =
  | { kind: "provisions"; record: PpeProvision | null; onClose: () => void }
  | { kind: "purchases"; record: PpePurchase | null; onClose: () => void };

const formatQuantity = (value: number) =>
  Number.isInteger(value)
    ? value.toLocaleString()
    : value.toLocaleString(undefined, { maximumFractionDigits: 4 });

const formatDate = (value?: string | null) =>
  value ? format(new Date(value), "MMM dd, yyyy") : "—";

const DetailField: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div>
    <h6 className="font-semibold text-sm text-gray-600">{label}</h6>
    <p className="font-normal text-base text-gray-900">{value || "—"}</p>
  </div>
);

const MobileDetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="flex items-start justify-between gap-4 px-4 py-3">
    <span className="shrink-0 text-sm text-gray-500">{label}</span>
    <span className="min-w-0 break-words text-right text-sm font-medium text-gray-900">
      {value || "—"}
    </span>
  </div>
);

const PpeRecordDetailsDrawer: React.FC<PpeRecordDetailsDrawerProps> = (
  props,
) => {
  const { kind, record, onClose } = props;
  const queryClient = useQueryClient();
  const [showUpload, setShowUpload] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [noteProvision, setNoteProvision] = useState<PpeProvision | null>(null);
  const [notePurchase, setNotePurchase] = useState<PpePurchase | null>(null);
  const isMobile = useIsMobile();

  const recordQuery = useQuery({
    queryKey: ["ppe", kind, "record", record?.id],
    queryFn: () => getPpeRecord(kind as PpeRecordKind, record!.id),
    enabled: Boolean(record?.id),
  });

  const documents: PpeRecordDocument[] =
    recordQuery.data?.documents ?? record?.documents ?? [];

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["ppe", kind] }),
      kind === "provisions"
        ? queryClient.invalidateQueries({ queryKey: ["ppe", "users"] })
        : Promise.resolve(),
    ]);
  };

  const handleDelete = async (doc: PpeRecordDocument) => {
    if (!record) return;
    if (!window.confirm(`Delete "${doc.fileName}"?`)) return;
    try {
      setDeletingId(doc.id);
      await deletePpeRecordDocument(kind, record.id, doc.id);
      toast.success("Document deleted");
      await refresh();
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to delete document"));
    } finally {
      setDeletingId(null);
    }
  };

  const ppe = record?.ppe;
  const itemName = ppe
    ? ppe.size
      ? `${ppe.name} (${ppe.size})`
      : ppe.name
    : "PPE/Non Scaffolds";
  const unit = ppe?.unit?.trim();
  const quantityLabel = record
    ? `${formatQuantity(Number(record.quantity))}${unit ? ` ${unit}` : ""}`
    : "—";

  const provision =
    kind === "provisions" ? (record as PpeProvision | null) : null;
  const purchase = kind === "purchases" ? (record as PpePurchase | null) : null;

  const title = provision
    ? `Provision of ${itemName} to ${provision.ppeUser?.fullName ?? "—"}`
    : `Purchase of ${itemName}`;

  const recordedLabel = record?.createdAt
    ? `Recorded: ${format(new Date(record.createdAt), "MMMM dd, yyyy 'at' hh:mm a")}`
    : "Recorded: —";

  const detailFields: { label: string; value: React.ReactNode }[] = [
    { label: "Quantity", value: quantityLabel },
    { label: "Warehouse", value: ppe?.warehouse?.name },
    ...(provision
      ? [
          { label: "Provided To", value: provision.ppeUser?.fullName },
          { label: "Job Title", value: provision.ppeUser?.jobTitle },
          { label: "Location", value: provision.ppeUser?.location },
          { label: "Provided Date", value: formatDate(provision.providedDate) },
          { label: "Provided By", value: provision.providedByUser?.fullName },
        ]
      : []),
    ...(purchase
      ? [
          {
            label: "Purchased Date",
            value: formatDate(purchase.purchasedDate),
          },
          { label: "Recorded By", value: purchase.purchasedByUser?.fullName },
        ]
      : []),
  ];

  const body = record && (
    <div
      className={
        isMobile ? "w-full p-4 space-y-4 pb-8" : "w-full p-6 space-y-6 pb-20"
      }
    >
      <div className="border border-gray-200 rounded-2xl">
        <div
          className={cn(
            "bg-gray-50 px-4 py-3 rounded-t-2xl flex items-center justify-between",
            isMobile && "gap-2",
          )}
        >
          <Badge variant="onhold" className="rounded-full">
            {provision ? "PROVISION" : "PURCHASE"}
          </Badge>
          {ppe?.condition && (
            <span className="text-sm text-gray-600">
              Condition: <span className="font-medium">{ppe.condition}</span>
            </span>
          )}
        </div>
        {isMobile ? (
          <div className="divide-y divide-gray-100">
            {detailFields.map((field) => (
              <MobileDetailRow
                key={field.label}
                label={field.label}
                value={field.value}
              />
            ))}
            {purchase?.notes && (
              <div className="px-4 py-3">
                <p className="text-sm text-gray-500">Notes</p>
                <p className="mt-1 whitespace-pre-wrap break-words text-sm text-gray-900">
                  {purchase.notes}
                </p>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="p-4 grid grid-cols-2 md:grid-cols-3 gap-4">
              {detailFields.map((field) => (
                <DetailField
                  key={field.label}
                  label={field.label}
                  value={field.value}
                />
              ))}
            </div>
            {purchase?.notes && (
              <div className="px-4 pb-4">
                <DetailField label="Notes" value={purchase.notes} />
              </div>
            )}
          </>
        )}
      </div>

      <div
        className={
          isMobile ? "flex flex-col gap-2" : "flex items-center gap-3 flex-wrap"
        }
      >
        {provision && (
          <Button
            variant="outlinePrimary"
            className={cn(isMobile && "w-full")}
            onClick={() => setNoteProvision(provision)}
          >
            Download Material Request Note
          </Button>
        )}
        {purchase && (
          <Button
            variant="outlinePrimary"
            className={cn(isMobile && "w-full")}
            onClick={() => setNotePurchase(purchase)}
          >
            Download Details of Received
          </Button>
        )}
        <Button
          variant="outlinePrimary"
          className={cn(isMobile && "w-full")}
          onClick={() => setShowUpload(true)}
        >
          Upload Documents
        </Button>
      </div>

      <Accordion
        type="single"
        collapsible
        defaultValue="documents"
        className="w-full"
      >
        <AccordionItem value="documents">
          <AccordionTrigger>
            <h4 className="text-base font-bold">
              Attachment Documents ({documents.length})
            </h4>
          </AccordionTrigger>
          <AccordionContent>
            {recordQuery.isLoading && documents.length === 0 ? (
              <div className="text-center text-gray-500 py-8">Loading...</div>
            ) : documents.length > 0 ? (
              documents.map((document) => (
                <div
                  key={document.id}
                  className={cn(
                    "flex items-center justify-between border-b last:border-b-0",
                    isMobile ? "gap-2 py-3" : "p-4",
                  )}
                >
                  <div
                    className={cn(
                      "flex items-center min-w-0",
                      isMobile ? "gap-3" : "space-x-4",
                    )}
                  >
                    <Icons.Document
                      className={cn(
                        "text-gray-400 shrink-0",
                        isMobile ? "w-8 h-8" : "w-10 h-10",
                      )}
                    />
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate">
                        {document.fileName}
                      </p>
                      <p className="text-gray-500 text-sm">
                        Uploaded{" "}
                        {format(
                          new Date(document.uploadedAt),
                          "MMM dd, yyyy 'at' hh:mm a",
                        )}
                      </p>
                      {document.mimeType && (
                        <p
                          className={cn(
                            "text-gray-400 text-xs mt-1",
                            isMobile && "truncate",
                          )}
                        >
                          {document.mimeType}
                        </p>
                      )}
                    </div>
                  </div>
                  <div
                    className={cn(
                      "flex shrink-0",
                      isMobile ? "gap-1" : "space-x-2",
                    )}
                  >
                    {document.filePath ? (
                      <>
                        <a
                          href={document.filePath}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 rounded-md hover:bg-gray-100"
                          title="Preview"
                        >
                          <Icons.Preview className="w-5 h-5 text-gray-600" />
                        </a>
                        <a
                          href={document.filePath}
                          target="_blank"
                          rel="noopener noreferrer"
                          download={document.fileName}
                          className="p-2 rounded-md hover:bg-gray-100"
                          title="Download"
                        >
                          <Download className="w-5 h-5 text-gray-600" />
                        </a>
                      </>
                    ) : (
                      <span className="text-gray-400 text-xs self-center">
                        No file available
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => handleDelete(document)}
                      disabled={deletingId === document.id}
                      className="p-2 rounded-md hover:bg-red-50 disabled:opacity-50"
                      title="Delete"
                    >
                      <Trash2 className="w-5 h-5 text-red-500" />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-gray-500 py-8">
                No documents attached
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );

  return (
    <>
      <PpeMaterialRequestNoteDrawer
        anchorProvision={noteProvision}
        onClose={() => setNoteProvision(null)}
      />
      <PpeDetailsOfReceivedDrawer
        anchorPurchase={notePurchase}
        onClose={() => setNotePurchase(null)}
      />
      <UploadDocumentModal
        open={showUpload && Boolean(record)}
        onClose={() => setShowUpload(false)}
        onUpload={(file) => uploadPpeRecordDocument(kind, record!.id, file)}
        onSuccess={async () => {
          toast.success("Document uploaded");
          await refresh();
        }}
      />

      <SidePanel
        open={!!record}
        onClose={onClose}
        width="w-[900px]"
        layer="base"
      >
        {record && (
          <div className="relative h-full flex flex-col">
            {isMobile ? (
              <PanelHeader
                title={title}
                subtitle={recordedLabel}
                onClose={onClose}
              />
            ) : (
              <div className="px-6 py-4 flex-shrink-0 border-b border-gray-100">
                <div className="flex items-start gap-3">
                  <h2 className="text-lg font-semibold text-foreground font-jakarta mr-auto">
                    {title}
                  </h2>
                  <Button
                    variant="outline"
                    className="h-8 w-8 p-0"
                    onClick={onClose}
                    aria-label="Close"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-sm text-gray-500 mt-1">{recordedLabel}</p>
              </div>
            )}

            {isMobile ? (
              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
                {body}
              </div>
            ) : (
              <ScrollArea className="flex-grow">{body}</ScrollArea>
            )}
          </div>
        )}
      </SidePanel>
    </>
  );
};

export default PpeRecordDetailsDrawer;
